import { useState, useEffect, useCallback } from 'react';
import { Folder, FileText, ChevronRight, ArrowUp, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { serversApi, DirectoryListResponse } from '../../lib/api/servers';

interface FileExplorerModalProps {
  isOpen: boolean;
  onClose: () => void;
  serverId: string;
  onSelect: (path: string) => void;
  title?: string;
  initialPath?: string;
}

export function FileExplorerModal({
  isOpen,
  onClose,
  serverId,
  onSelect,
  title = 'Select File',
  initialPath = '/',
}: FileExplorerModalProps) {
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [directoryData, setDirectoryData] = useState<DirectoryListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const loadDirectory = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    setSelectedFile(null);

    try {
      const data = await serversApi.listDirectory(serverId, path);
      if (data.error) {
        setError(data.error);
      } else {
        setDirectoryData(data);
        setCurrentPath(data.path);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => {
    if (isOpen) {
      loadDirectory(initialPath);
    }
  }, [isOpen, initialPath, loadDirectory]);

  const handleFolderClick = (folderName: string) => {
    const newPath = currentPath === '/'
      ? `/${folderName}`
      : `${currentPath}/${folderName}`;
    loadDirectory(newPath);
  };

  const handleGoUp = () => {
    if (directoryData?.parent) {
      loadDirectory(directoryData.parent);
    }
  };

  const handleFileClick = (fileName: string) => {
    const fullPath = currentPath === '/'
      ? `/${fileName}`
      : `${currentPath}/${fileName}`;
    setSelectedFile(fullPath);
  };

  const handleSelect = () => {
    if (selectedFile) {
      onSelect(selectedFile);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setCurrentPath(initialPath);
    setDirectoryData(null);
    onClose();
  };

  // Parse path into breadcrumb segments
  const pathSegments = currentPath === '/'
    ? ['/']
    : ['/', ...currentPath.split('/').filter(Boolean)];

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      loadDirectory('/');
    } else {
      const newPath = '/' + pathSegments.slice(1, index + 1).join('/');
      loadDirectory(newPath);
    }
  };

  const isHidden = (name: string) => name.startsWith('.');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="xl">
      <div className="flex flex-col" style={{ height: '60vh' }}>
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 rounded-lg mb-3 overflow-x-auto flex-shrink-0">
          <span className="text-sm text-gray-500 shrink-0">Path:</span>
          {pathSegments.map((segment, index) => (
            <div key={index} className="flex items-center shrink-0">
              {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400" />}
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className="px-1.5 py-0.5 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors"
              >
                {segment}
              </button>
            </div>
          ))}
        </div>

        {/* File List */}
        <div className="flex-1 border rounded-lg overflow-y-auto bg-white min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-600 mb-2">{error}</p>
                <Button variant="outline" size="sm" onClick={() => loadDirectory(currentPath)}>
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <div className="divide-y">
              {/* Go up button */}
              {currentPath !== '/' && (
                <button
                  onClick={handleGoUp}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <ArrowUp className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">..</span>
                  <span className="text-gray-400 text-sm">(parent directory)</span>
                </button>
              )}

              {/* Folders */}
              {directoryData?.folders.map((folder) => (
                <button
                  key={folder}
                  onClick={() => handleFolderClick(folder)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <Folder className="h-5 w-5 text-amber-500" />
                  <span className={isHidden(folder) ? 'text-gray-500' : 'text-gray-900'}>
                    {folder}
                  </span>
                </button>
              ))}

              {/* Files */}
              {directoryData?.files.map((file) => {
                const fullPath = currentPath === '/' ? `/${file}` : `${currentPath}/${file}`;
                const isSelected = selectedFile === fullPath;

                return (
                  <button
                    key={file}
                    onClick={() => handleFileClick(file)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                      isSelected
                        ? 'bg-primary-50 border-l-4 border-primary-600'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <FileText className={`h-5 w-5 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                    <span className={
                      isSelected
                        ? 'text-primary-900 font-medium'
                        : isHidden(file)
                          ? 'text-gray-500'
                          : 'text-gray-900'
                    }>
                      {file}
                    </span>
                  </button>
                );
              })}

              {/* Empty state */}
              {directoryData && directoryData.folders.length === 0 && directoryData.files.length === 0 && (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  This directory is empty
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected file display and actions */}
        <div className="mt-3 pt-3 border-t flex-shrink-0">
          {selectedFile && (
            <div className="mb-3 p-2 bg-primary-50 rounded text-sm">
              <span className="text-gray-500">Selected: </span>
              <span className="text-primary-700 font-medium break-all">{selectedFile}</span>
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSelect} disabled={!selectedFile}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
